<template lang="pug">
  v-dialog(data-cy="egister-sole-propiertor-modal" persistent :value="value" max-width="780")
    liondesk-dialog-v2(
      v-model="showCloseDialog"
      dialog-title="Close Form?"
      ok-button-text="CLOSE"
      @click:ok="closeCloseDialog"
      @click:cancel="showCloseDialog = false"
      rounded
    )
      template(slot="body")
        div Closing this form means you'll lose your registration information and have to re-enter again later. Are you sure you want to continue?

    v-card.rounded-card
      v-card-title
        v-layout(row justify-center)
          v-flex(xs12)
            h1.primary--text.text-xs-center.font-weight-thin.title-pad.mt-2 {{ bannerText }}
      v-card-text.pt-2.pb-0
        v-layout.mt-1(column)
          v-flex(v-if="isSolePropietorStep")
            v-layout(row)
              v-flex(xs12)
                p {{ p1Text }}
                p(v-html="p2Text")
            v-layout(v-if="hasRegistrationError" row)
              v-flex(xs12)
                custom-error-registration(
                  :errors="registrationErrors"
                  :registrationStatus10dlc="registrationStatus10dlc"
                  :registrationCampaignRetries="registrationCampaignRetries"
                  )
            v-layout(row)
              v-flex(xs12)
                a2p-register-sole-proprietor-form(@form:save="formSaved")
          v-flex(v-if="isCampaignStep")
            v-layout(row)
              v-flex(xs12)
                p(v-html="p1CampaignText")
            v-layout(v-if="hasRegistrationError" row)
              v-flex(xs12)
                custom-error-registration(
                  :errors="registrationErrors"
                  :registrationStatus10dlc="registrationStatus10dlc"
                  :registrationCampaignRetries="registrationCampaignRetries"
                )
            v-layout(row)
              v-flex(xs12)
                a2p-campaign-sole-proprietor(:disable-submit="false" @form:save="formSaved")
        v-layout(column align-center)
          v-btn.close-button(@click="displayCloseDialog") Close
          v-flex.mt-1(xs12)
            a.text-xs-center(
              href="https://lonewolf.my.site.com/s/article/LD-FAQ-Brand-or-business-registration-for-texting-LionDesk"
              target="_blank"
            ) Learn more about why wireless carriers are requiring identity registration.
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { STEPS_SOLE_PROPRIETOR } from '@/a2p/config';

const a2pRegisterSoleProprietorForm = () =>
  import(
    /* webpackChunkName: 'a2pRegisterSoleProprietorForm' */ '@/a2p/components/RegisterSoleProprietorForm'
  );

const a2pCampaignSoleProprietor = () =>
  import(
    /* webpackChunkName: 'a2pCampaignSoleProprietor' */ '@/a2p/components/CampaignSoleProprietor'
  );

const CustomErrorRegistration = () =>
  import(
    /* webpackChunkName: 'CustomErrorRegistration' */ '@/Core/components/CustomErrorRegistration'
  );

const LiondeskDialogV2 = () =>
  import(/* webpackChunkName: 'LiondeskDialogV2' */ '@/Core/components/LiondeskDialogV2');

export default {
  name: 'RegisterSoleProprietorModal',
  components: {
    a2pRegisterSoleProprietorForm,
    a2pCampaignSoleProprietor,
    CustomErrorRegistration,
    LiondeskDialogV2,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      steps: STEPS_SOLE_PROPRIETOR,
      showCloseDialog: false,
    };
  },
  created() {
    if (!this.has10dlcRegister || this.isRegistration10dlcIncomplete) {
      this.checkSoleProprietorRegistration();
    }
  },
  methods: {
    ...mapActions('conversations', ['checkSoleProprietorRegistration']),
    displayCloseDialog() {
      if (this.has10dlcRegister) {
        this.closeModal();
        return;
      }
      this.showCloseDialog = true;
    },
    closeCloseDialog() {
      this.showCloseDialog = false;
      this.closeModal();
    },
    closeModal() {
      this.$emit('close');
    },
    async formSaved() {
      this.$emit('show-register-fee-modal');
    },
  },
  computed: {
    ...mapState('registerSoleProprietorA2p', ['step']),
    ...mapState('conversations', [
      'has10dlcRegister',
      'registrationStatus10dlc',
      'registrationStatusData10dlc',
      'registrationBrandRetries',
      'registrationCampaignRetries',
      'isRegistration10dlcIncomplete',
    ]),
    bannerText() {
      return this.isSolePropietorStep ? 'Verify Your Identity' : 'Text Message Types';
    },
    p1Text() {
      return `Register your personal identity with US wireless carriers to validate your SMS traffic and ensure your communications get delivered.`;
    },
    p2Text() {
      return `Please complete this short form to verify your personal identity. Follow the exact instructions to avoid registration failures.`;
    },
    p1CampaignText() {
      return '<strong>What is a campaign?</strong> A campaign represents a use case, including texts sent out immediately, scheduled texts and texts in a campaign. This informs the carrier the type of content you are sending to your contacts.';
    },
    isSolePropietorStep() {
      return this.step === this.steps.SOLE_PROPRIETOR;
    },
    isCampaignStep() {
      return this.step === this.steps.CAMPAIGN;
    },
    hasRegistrationError() {
      const registrationError = [
        'campaign_tcr_failed',
        'campaign_twilio_failed',
        'campaign_blocked_failed',
        'brand_failed',
        'brand_tcr_failed',
        'a2p_bundle_failed',
        'customer_profile_failed',
      ];
      return (
        registrationError.includes(this.registrationStatus10dlc) &&
        this.registrationStatusData10dlc &&
        !!this.registrationStatusData10dlc.length
      );
    },
    registrationErrors() {
      let errors = [];
      if (this.hasRegistrationError) {
        errors = errors.concat(this.registrationStatusData10dlc.map(x => x.description));
      }

      if (['brand_tcr_failed'].includes(this.registrationStatus10dlc)) {
        errors = errors.concat(this.brandTCRFailedErrors);
      }

      return errors;
    },
    brandTCRFailedErrors() {
      let errors = [];
      if (this.registrationBrandRetries < 3) {
        let attempt = ['first', 'second', 'final'];
        errors = errors.concat([`<strong>This is your ${attempt[this.registrationBrandRetries]} retry attempt</strong>. You may retry up to 3 times after your initial identity submission before a new identity verification fee is incurred. Please follow exact form instructions to avoid errors.`]);
      } else {
        errors = errors.concat([`<strong>This submission will restart your identity registration</strong>. US carriers will charge you a verification restart fee to continue.`])
      }
      if (this.registrationBrandRetries > 0) {
        errors = errors.concat([`Need help fixing your registration? <a href='${this.errorMailString}' target='_blank'>Contact support</a> for assistance.`]);
      }
      return errors;
    },
    errorMailString() {
      let errors = '';
      if (this.registrationStatusData10dlc) {
        errors = `${this.registrationStatusData10dlc.map(x => x.description).join('\n\n')}`;
      }
      let attempt = ['', '2nd Retry', '3rd Retry', 'Restart'];
      var subject = encodeURIComponent(`Texting: Sole Proprietor registration assistance - ${attempt[this.registrationBrandRetries]}`);
      var body = encodeURIComponent(`My Sole Proprietor registration is returning this error after multiple attempts:\n\n${errors}`);
      return `mailto:support@liondesk.com?subject=${subject}&body=${body}`
    },
  },
};
</script>

<style lang="scss" scoped>
.action-btn {
  height: 60px;
}

.rounded-card {
  border-radius: 10px;
  padding-bottom: 10px;
}

.title-pad {
  padding-left: 40px;
  padding-right: 40px;
  text-transform: none;
  margin-bottom: -10px;
}

.close-button {
  color: var(--v-primary-base);
  background-color: transparent !important;
}
</style>
